import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { RoleService } from '../services/role.service';
import { deleteProjectDialog, showError, successDialog } from 'src/app/shared/dialog';

@Component({
  selector: 'app-create-roles',
  templateUrl: './role-form.component.html',
  styleUrls: ['./role-form.component.css']
})
export class RoleFormComponent implements OnInit {

  mode: 'create' | 'edit' | 'view' = 'create';

  projectId!: string | null;
  roleLevel: string | null = null;

  roleId!: string;               // FROM localStorage
  userRoleId!: string;           // FROM route


  roleForm = new FormGroup({
    roleName: new FormControl('', [Validators.required, Validators.minLength(3)]),
    description: new FormControl('')
  });

  allPermissions: string[] = [];

  // 🔥 NEW: scope → entity → permissions
  permissionTree: {
    [scope: string]: {
      [entity: string]: string[]
    }
  } = {};

  selectedPermissions: Set<string> = new Set();

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private roleService: RoleService
  ) { }

  // ---------------------------------------------------------
  // INIT
  // ---------------------------------------------------------
  ngOnInit(): void {
    this.detectMode();

    this.projectId = this.route.snapshot.paramMap.get('projectId');

    this.roleId = localStorage.getItem('roleId') || '';
    this.userRoleId = this.route.snapshot.paramMap.get('id') || '';

    if (!this.roleId) {
      console.error('roleId not found in localStorage');
      return;
    }

    this.loadAvailablePermissions();
  }

  // ---------------------------------------------------------
  // MODE DETECTION
  // ---------------------------------------------------------
  private detectMode() {
    const url = this.router.url;

    if (url.includes('create-role')) this.mode = 'create';
    else if (url.includes('role-edit')) this.mode = 'edit';
    else if (url.includes('role-details')) this.mode = 'view';
  }

  // ---------------------------------------------------------
  // LOAD AVAILABLE PERMISSIONS
  // ---------------------------------------------------------
  private loadAvailablePermissions(): void {
    this.roleService.getRolePermissions(this.roleId).subscribe({
      next: (res: any) => {
        this.allPermissions = res.permissions || [];
        this.permissionTree = this.groupPermissions(this.allPermissions);

        if (this.mode !== 'create') {
          this.loadUserPermissions();
        }
      },
      error: err => console.error('Error loading role permissions', err)
    });
  }

  // ---------------------------------------------------------
  // LOAD USER PERMISSIONS
  // ---------------------------------------------------------
  private loadUserPermissions(): void {
    this.roleService.getRolePermissions(this.userRoleId).subscribe({
      next: (res: any) => {
        this.roleForm.patchValue({
          roleName: res.roleName,
          description: res.description || ''
        });
        this.roleLevel = res.roleLevel || null;

        this.selectedPermissions = new Set(res.permissions || []);

        if (this.mode === 'view') {
          this.roleForm.disable();
        }
      },
      error: err => console.error('Error loading user permissions', err)
    });
  }

  // ---------------------------------------------------------
  // GROUP PERMISSIONS (scope → entity)
  // ---------------------------------------------------------
  private groupPermissions(perms: string[]) {
    const tree: {
      [scope: string]: {
        [entity: string]: string[]
      }
    } = {};

    perms.forEach(p => {
      const parts = p.split('.');

      const scope = parts[0];
      const entity = parts[1] || 'general';

      if (!tree[scope]) {
        tree[scope] = {};
      }

      if (!tree[scope][entity]) {
        tree[scope][entity] = [];
      }

      tree[scope][entity].push(p);
    });

    return tree;
  }

  // ---------------------------------------------------------
  // PERMISSION TOGGLE
  // ---------------------------------------------------------
  togglePermission(permission: string) {
    if (this.mode === 'view') return;

    this.selectedPermissions.has(permission)
      ? this.selectedPermissions.delete(permission)
      : this.selectedPermissions.add(permission);
  }

  isPermissionSelected(p: string) {
    return this.selectedPermissions.has(p);
  }

  // ---------------------------------------------------------
  // ENTITY LEVEL
  // ---------------------------------------------------------
  toggleEntity(scope: string, entity: string) {
    if (this.mode === 'view') return;

    const perms = this.permissionTree[scope][entity];
    const allSelected = perms.every(p => this.selectedPermissions.has(p));

    perms.forEach(p =>
      allSelected
        ? this.selectedPermissions.delete(p)
        : this.selectedPermissions.add(p)
    );
  }

  isEntitySelected(scope: string, entity: string): boolean {
    return this.permissionTree[scope][entity]
      .every(p => this.selectedPermissions.has(p));
  }

  isEntityIndeterminate(scope: string, entity: string): boolean {
    const perms = this.permissionTree[scope][entity];
    const selected = perms.filter(p => this.selectedPermissions.has(p));
    return selected.length > 0 && selected.length < perms.length;
  }

  // ---------------------------------------------------------
  // SCOPE LEVEL
  // ---------------------------------------------------------
  toggleScope(scope: string) {
    if (this.mode === 'view') return;

    const perms = Object.values(this.permissionTree[scope]).flat();
    const allSelected = perms.every(p => this.selectedPermissions.has(p));

    perms.forEach(p =>
      allSelected
        ? this.selectedPermissions.delete(p)
        : this.selectedPermissions.add(p)
    );
  }

  // ---------------------------------------------------------
  // CREATE
  // ---------------------------------------------------------
  createRole(): void {
    if (this.roleForm.invalid || this.selectedPermissions.size === 0) return;

    const payload = {
      name: this.roleForm.value.roleName!.toUpperCase(),
      description: this.roleForm.value.description || '',
      projectId: this.projectId
    };

    this.roleService.createRole(this.projectId!, payload).subscribe({
      next: (response) => {
        this.savePermissions(response.roleId);
      }
    });
  }

  // ---------------------------------------------------------
  // SAVE
  // ---------------------------------------------------------
  savePermissions(roleId = this.userRoleId) {
    this.roleService.assignPermissionsBulk({
      roleId,
      permissions: Array.from(this.selectedPermissions)
    }).subscribe({
      next: () => this.backToRoles()
    });
  }

  // ---------------------------------------------------------
  // NAVIGATION
  // ---------------------------------------------------------
  backToRoles() {
    this.router.navigate(['/project-roles', this.projectId, 'roles']);
  }

  goToEdit() {
    this.router.navigate(['/project-roles', this.projectId, 'role-edit', this.userRoleId]);
  }

  goToView() {
    this.router.navigate(['/project-roles', this.projectId, 'role-details', this.userRoleId]);
  }

  deleteRole() {
    deleteProjectDialog().then(res => {
      if (!res.isConfirmed) return;

      this.roleService.deleteRole(this.userRoleId).subscribe({
        next: () => {
          successDialog('Role deleted successfully');
          this.backToRoles();
        },
        error: err => showError(err?.error?.message || 'Delete failed')
      });
    });
  }

  // ---------------------------------------------------------
  // SELECT / DESELECT ALL
  // ---------------------------------------------------------
  selectAllPermissions(): void {
    if (this.mode === 'view') return;
    this.selectedPermissions = new Set(this.allPermissions);
  }

  deselectAllPermissions(): void {
    if (this.mode === 'view') return;
    this.selectedPermissions.clear();
  }

  isScopeSelected(scopeKey: string): boolean {
    const entities = this.permissionTree[scopeKey];
    if (!entities) return false;

    const allPermissions = Object.values(entities).flat();
    return allPermissions.every(p => this.selectedPermissions.has(p));
  }

}
